import '@web/library/dev-globals';
import 'nprogress/nprogress.css';
import '../globals.css';

import { ComponentType, ReactNode, useEffect, useMemo } from 'react';
import type { AppProps } from 'next/app';
import { I18nextProvider } from 'react-i18next';
import { initI18n } from 'shared/library/i18n/init-i18n';
import { Router, useRouter } from 'next/router';
import NProgress from 'nprogress';
import { UserProvider } from 'shared/state/user';
import { BreederProvider } from 'shared/state/breeder';
import Script from 'next/script';
import { useEffectOnce } from 'shared/library/use-effect-once';
import { env } from 'shared/library/env';
import { monitoring } from 'shared/library/monitoring';
import { Plus_Jakarta_Sans } from 'next/font/google';
import { AppErrorBoundary } from '@web/components/app-error-boundary';
import { AuthSessionSync } from '@web/components/auth-session-sync';
import { usePreserveScroll } from '@web/library/use-preserve-scroll';
import { DefaultProps } from '@web/library/with-server-props';
import { AuthGuard } from 'shared/components/auth-guard';
import { UIVisibilityProvider } from 'shared/state/ui-visibility';
import { RouteProvider } from 'shared/library/routes/provider';
import { ShopData, ShopProvider, useShopStore } from 'shared/state/shop';
import { getSafeLanguageCode } from 'shared/library/i18n/languages';
import dynamic from 'next/dynamic';
import { DeferredClientRender } from '@web/components/deferred-client-render';
import { reloadOnChunkLoadError } from '@web/library/chunk-load-recovery';
import { recordAttribution } from 'shared/library/attribution';
import { useCookieConsent } from 'shared/library/cookie-consent';

NProgress.configure({ showSpinner: false, speed: 400 });

Router.events.on('routeChangeStart', () => NProgress.start());
Router.events.on('routeChangeComplete', () => NProgress.done());
Router.events.on('routeChangeError', (error) => {
  NProgress.done();
  reloadOnChunkLoadError(error);
});

const jakarta = Plus_Jakarta_Sans({ subsets: ['latin'], variable: '--font-jakarta' });
const DataCenter = dynamic(
  () => import('shared/components/data-center').then((mod) => mod.DataCenter),
  {
    ssr: false,
  },
);
const CookieConsent = dynamic(
  () => import('@web/components/cookie-consent').then((mod) => mod.CookieConsent),
  { ssr: false },
);
const Toaster = dynamic(
  () => import('shared/library/notify/toaster.web').then((mod) => mod.Toaster),
  { ssr: false },
);

type AppPageProps = Partial<DefaultProps> & Record<string, unknown>;

function getFallbackDefaultProps(locale?: string | null): DefaultProps['defaultProps'] {
  return {
    routeParams: {},
    requiresAuth: false,
    requiresBreeder: false,
    shopData: {
      productCategories: [],
      settings: null,
    },
    languageCode: getSafeLanguageCode(locale),
    translations: {},
    apiKey: null,
    user: null,
    breeder: null,
  };
}

export default function App({ pageProps, Component }: AppProps<AppPageProps>) {
  const router = useRouter();
  const fallbackDefaultProps = useMemo(
    () => getFallbackDefaultProps(router.locale),
    [router.locale],
  );
  const defaultProps = pageProps.defaultProps ?? fallbackDefaultProps;
  const i18n = useMemo(
    () => initI18n(defaultProps.languageCode, defaultProps.translations),
    [defaultProps.languageCode, defaultProps.translations],
  );
  const SentryErrorBoundary = monitoring.Sentry?.ErrorBoundary as
    | ComponentType<{ children: ReactNode }>
    | undefined;
  const hasGoogleAnalytics = env.GOOGLE_ANALYTICS_ID !== '';
  const hasMarketingConsent = useCookieConsent()?.marketing === true;
  const hasMetaPixel = env.META_PIXEL_ID !== '' && hasMarketingConsent;
  // Guest previews render without a session, including when a login cookie exists.
  const isGuestPreview =
    env.IS_DEV && ['/dev/contact-intro', '/dev/contact-flow'].includes(router.pathname);

  usePreserveScroll();

  useEffect(() => {
    const record = (url?: string) => {
      void recordAttribution(
        url ? new URL(url, window.location.origin).href : window.location.href,
      );
    };

    record();
    Router.events.on('routeChangeComplete', record);

    return () => {
      Router.events.off('routeChangeComplete', record);
    };
  }, []);

  useEffectOnce(() => {
    const brevoTranslations = i18n.getResourceBundle(defaultProps.languageCode, 'library')?.brevo;

    import('shared/library/brevo').then(({ brevo }) => {
      brevo.identifyUser(defaultProps.user);
      brevo.setTranslations(brevoTranslations);
    });
  });

  const appContent = (
    <>
      <I18nextProvider i18n={i18n}>
        <RouteProvider params={defaultProps.routeParams}>
          <UserProvider key={isGuestPreview ? router.pathname : 'app'} initialState={defaultProps}>
            {!isGuestPreview && <AuthSessionSync />}
            <BreederProvider initialState={defaultProps}>
              <ShopProvider initialState={defaultProps.shopData}>
                <ShopStateSync shopData={defaultProps.shopData} />
                <UIVisibilityProvider>
                  <AuthGuard defaultProps={defaultProps}>
                    <DeferredClientRender timeout={2000}>
                      <DataCenter />
                    </DeferredClientRender>

                    <div className={`${jakarta.variable}`}>
                      <AppErrorBoundary>
                        <Component {...pageProps} defaultProps={defaultProps} />
                      </AppErrorBoundary>
                      <CookieConsent />
                    </div>

                    <DeferredClientRender>
                      <Toaster
                        position="top-right"
                        offset={{
                          top: '80px',
                        }}
                        mobileOffset={{
                          top: '64px',
                        }}
                      />
                    </DeferredClientRender>

                    {/* Queue tracking immediately; download SDKs after load, during idle time. */}
                    {/* Google Analytics */}
                    {hasGoogleAnalytics && (
                      <>
                        <Script id="ga4-init" strategy="afterInteractive">
                          {`
                            window.dataLayer = window.dataLayer || [];
                            window.gtag = window.gtag || function(){window.dataLayer.push(arguments);}
                            window.gtag('js', new Date());
                            window.gtag('config', '${env.GOOGLE_ANALYTICS_ID}', {
                              page_path: window.location.pathname,
                              debug_mode: ${env.IS_DEV},
                            });
                          `}
                        </Script>
                        <Script
                          src={`https://www.googletagmanager.com/gtag/js?id=${env.GOOGLE_ANALYTICS_ID}`}
                          strategy="lazyOnload"
                        />
                      </>
                    )}

                    {/* Meta Pixel */}
                    {hasMetaPixel && (
                      <>
                        <Script id="meta-pixel" strategy="afterInteractive">
                          {`
                            !function(f,n)
                            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                            n.queue=[]}(window);
                            fbq('consent', 'grant');
                            fbq('set', 'autoConfig', 'false', '${env.META_PIXEL_ID}');
                            fbq('set', 'smartSetup', 'false', '${env.META_PIXEL_ID}');
                            fbq('init', '${env.META_PIXEL_ID}');
                            fbq('track', 'PageView');
                          `}
                        </Script>
                        <Script
                          src="https://connect.facebook.net/en_US/fbevents.js"
                          strategy="lazyOnload"
                        />
                      </>
                    )}

                    {/* ChatGPT Ads Pixel */}
                    {hasMarketingConsent && (
                      <>
                        <Script id="chatgpt-pixel" strategy="afterInteractive">
                          {`
                            !function(w){if(w.oaiq)return;var q=function(){q.q.push(arguments)};
                            q.q=[];w.oaiq=q}(window);
                            oaiq("init",{pixelId:"QQVMXmD9d6wh9F9AMZJYJM",debug:${env.IS_DEV}});
                          `}
                        </Script>
                        <Script
                          src="https://bzrcdn.openai.com/sdk/oaiq.min.js"
                          strategy="lazyOnload"
                        />
                      </>
                    )}
                  </AuthGuard>
                </UIVisibilityProvider>
              </ShopProvider>
            </BreederProvider>
          </UserProvider>
        </RouteProvider>
      </I18nextProvider>
    </>
  );

  if (SentryErrorBoundary) {
    return <SentryErrorBoundary>{appContent}</SentryErrorBoundary>;
  }

  return appContent;
}

function ShopStateSync({ shopData }: { shopData: ShopData }) {
  const setProductCategories = useShopStore((state) => state.setProductCategories);
  const setSettings = useShopStore((state) => state.setSettings);

  useEffect(() => {
    setProductCategories(shopData.productCategories ?? []);
    setSettings(shopData.settings ?? null);
  }, [shopData, setProductCategories, setSettings]);

  return null;
}
